import React from 'react';
import Typography from '@components/Atoms/Typography';
import Box from '@Atoms/Box';
import ShowIf from '@Atoms/ShowIf';
import { darken } from '@mui/material';
import { TabProvider, Tab as TabAbstract } from '../tabs';
import { useCurrentTab } from '../tabs/context/TabsContext';
import { TabPanel } from './TabsCatalogueSkin.styles';

const Tab = ({ children, ...props }) => (
  <TabAbstract {...props}>
    <Typography
      variant="h6"
      component="p"
      fontWeight="bold"
      textTransform="capitalize"
      noWrap
      sx={{
        color: 'text.secondary',
        '.react-tabs__tab--selected &': {
          color: 'text.primary',
        },
      }}
    >
      {children}
    </Typography>
  </TabAbstract>
);
const getBoxShadow = (activeShadowBefore, activeShadowAfter) => {
  if (activeShadowBefore) {
    return '-3px 0px 5px 0px #00000087';
  }
  if (activeShadowAfter) {
    return '3px 0px 5px 0px #00000087';
  }
  return 'none';
};
const TabDivider = ({ activeShadowBefore, activeShadowAfter }) => (
  <Box
    sx={{
      width: '2px',
      height: '100%',
      backgroundColor: 'divider',
      zIndex: 10,
      minHeight: '34px',
      minWidth: '2px',
      boxShadow: getBoxShadow(activeShadowBefore, activeShadowAfter),
    }}
  />
);
const TabList = ({ children, ...props }) => {
  const activeTab = useCurrentTab();
  const childrenArray = React.Children.toArray(children);
  return (
    <Box
      role="tablist"
      tabIndex={0}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'start',
        alignItems: 'stretch',
        width: '100%',
        height: 'min-content',
        overflow: 'auto',
        '& > .react-tabs__tab': {
          listStyle: 'none',
          display: 'flex',
          backgroundColor: (theme) => darken(theme.palette.background.paper, 0.01),
          paddingInline: 1.5,
          paddingBlock: 1,
          position: 'relative',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&:before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '0.1px',
            boxShadow: '0px 0px 5px 0px #00000087',
          },
          '&.react-tabs__tab--selected': {
            backgroundColor: 'background.paper',
            zIndex: 20,
            '&:before': {
              boxShadow: 'none',
            },
          },
          '&:first-of-type': {
            borderTopLeftRadius: 8,
          },
          '&:last-of-type': {
            borderTopRightRadius: 8,
          },
        },
      }}
    >
      {childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          <ShowIf If={index !== childrenArray.length - 1}>
            <TabDivider
              activeShadowBefore={activeTab === childrenArray[index + 1]?.props?.tabKey}
              activeShadowAfter={activeTab === child?.props?.tabKey}
            />
          </ShowIf>
        </React.Fragment>
      ))}
    </Box>
  );
};
const TabPanelList = ({ children, ...props }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      backgroundColor: 'background.paper',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'auto',
    }}
  >
    {children}
  </Box>
);
export default TabProvider;
export { TabProvider, Tab, TabPanel, TabList, TabPanelList };
